/**
 * Legal document constants.
 *
 * Every party name, address, contact, and date that appears in the Privacy
 * Policy and Terms of Service lives here, so correcting one is a one-line
 * change rather than a hunt through prose.
 *
 * ⚠️ LEGAL_ENTITY must match the IRS EIN registration (the CP-575 letter)
 * character-for-character. The identical string goes on the Twilio A2P brand
 * registration, where a mismatch is the single most common rejection cause.
 * Confirmed by Dylan 2026-07-26 — do not "tidy" the casing or add a suffix.
 */

/**
 * Registered legal entity operating ProgramRow — as filed with the Pennsylvania
 * Department of State. This is the entity that exists in law and can be party to
 * a contract, so it is the correct name for these documents.
 *
 * ⚠️ It is deliberately NOT the same string as TWILIO_BRAND_LEGAL_NAME below.
 * Do not "fix" the inconsistency — see that constant for why.
 */
export const LEGAL_ENTITY = 'Digital Video Technology LLC';

/**
 * Defined short form, introduced by the parenthetical in each document's first
 * paragraph. The binding identification is LEGAL_ENTITY above.
 */
export const LEGAL_ENTITY_SHORT = 'Digital Video Technology';

/**
 * The name to enter on the Twilio A2P 10DLC brand registration. NOT used
 * anywhere in the app — recorded here so the two names live side by side and
 * the difference is documented rather than rediscovered.
 *
 * The IRS CP-575 for this single-member LLC reads across two name lines:
 *
 *     DIGITAL VIDEO TECHNOLOGY
 *     DYLAN MCKENNA SOLE MBR
 *
 * Line 1 is the entity name; line 2 is the responsible-party line the IRS adds
 * for single-member LLCs. TCR matches the brand's legal name + EIN against IRS
 * records, so line 1 is what goes in the field — with neither the owner's name,
 * the "SOLE MBR" designation, nor an "LLC" suffix the IRS record does not carry.
 *
 * A mismatch here is the single most common brand-registration rejection cause.
 */
export const TWILIO_BRAND_LEGAL_NAME = 'DIGITAL VIDEO TECHNOLOGY';

/** Registered business address, as filed with the IRS. */
export const LEGAL_ADDRESS = 'Pittsburgh, Pennsylvania, United States';

/** US state whose law governs, and the county for venue. */
export const GOVERNING_STATE = 'Pennsylvania';
export const VENUE_COUNTY = 'Allegheny County, Pennsylvania';

export const CONTACT_PRIVACY = 'privacy@programrow.com';
export const CONTACT_SUPPORT = 'support@programrow.com';
export const CONTACT_LEGAL = 'legal@programrow.com';
/** Alternative payment arrangements (invoice/PO) and billing questions. Mailbox must exist in Cloudflare Email Routing. */
export const CONTACT_BILLING = 'billing@programrow.com';

/**
 * Shown at the top of both documents. Bump whenever the substance changes —
 * material changes also require notice to users under both documents.
 */
export const LEGAL_LAST_UPDATED = 'October 20, 2026';
export const LEGAL_EFFECTIVE_DATE = 'July 27, 2026';

/**
 * The exact SMS consent disclosure shown next to the opt-in checkbox.
 *
 * This string is quoted verbatim in the A2P 10DLC campaign registration and is
 * stored on the user record at opt-in time (User.smsConsentText) as evidence of
 * what each person actually agreed to. Changing the wording means bumping
 * SMS_CONSENT_VERSION so existing consent records stay attributable to the text
 * that was really displayed — never edit this in place.
 */
export const SMS_CONSENT_VERSION = 'v1';
export const SMS_CONSENT_TEXT =
  'I agree to receive text messages from ProgramRow about my crew assignments ' +
  'and schedule changes. Message frequency varies. Message and data rates may ' +
  'apply. Reply STOP to opt out or HELP for help. See our Privacy Policy and ' +
  'Terms of Service.';
